import { startCase } from "lodash";
import type { FileRejection } from "react-dropzone";

import { Select, Text } from "@alpacahq/ui/old";

import {
  FILE_ACCEPTED_TYPES,
  FILE_MAX_SIZE,
  FILE_SIZE_LABEL,
  FileDropzone,
} from "~/v2/components/fileUpload";
import {
  ID_TYPES,
  type ImageSide,
  type UseUploadIDReturn,
} from "./useUploadID";

export const ID_OPTIONS = Object.values(ID_TYPES).map(({ label, value }) => ({
  label,
  value,
}));

// used if ID already exists
export type ExistingID = {
  id: string;
  name: string;
};

export interface UploadIDProps extends UseUploadIDReturn {}

export const UploadID = ({
  id,
  idType,
  setIDType,
  onUpload,
  onRemove,
  onError,
}: UploadIDProps) => {
  const handleUpload = (side: ImageSide) => (acceptedFile: File) => {
    onUpload(acceptedFile, side);
  };

  const handleRemove = (side: ImageSide) => () => {
    onRemove(side);
  };

  const handleError = (side: ImageSide) => (fileRejection: FileRejection) => {
    onError(fileRejection, side);
  };

  const imageRequirement = idType ? ID_TYPES[idType].imageRequirement : [];

  return (
    <div className="flex flex-col gap-3">
      <Text className="font-semibold">Upload Photo ID</Text>
      <Text>
        Upload a copy of a valid, government-issued photo ID. This can be a
        national ID card, driver's license, passport, or residence permit.
      </Text>

      <label htmlFor="id_type" className="font-semibold">
        Photo ID Type
      </label>
      <Select
        id="id_type"
        value={idType}
        options={ID_OPTIONS}
        onChange={setIDType}
      />

      {imageRequirement.map((side) => {
        const sideText = startCase(side);

        return (
          <div key={side} className="flex flex-col gap-2">
            <Text className="font-semibold">{sideText}</Text>
            {side === "back" && (
              <Text className="text-gray-800 text-sm">
                Required for driver's licenses, permanent residence cards, and
                national identity cards.
              </Text>
            )}
            <FileDropzone
              file={id[side]}
              onUpload={handleUpload(side)}
              onRemove={handleRemove(side)}
              onError={handleError(side)}
              label={`${sideText} of ID`}
              isRequired
              placeholder={`${sideText} ID`}
              name={`id_${side}`}
              maxSize={FILE_MAX_SIZE}
              accept={FILE_ACCEPTED_TYPES}
              displayType="image"
              maxSizeLabel={FILE_SIZE_LABEL}
            />
          </div>
        );
      })}
    </div>
  );
};
